/**
 * Streaming Code Accumulator
 * Buffers and parses incoming code chunks from AI streaming responses
 * Handles partial chunks and delimiter boundaries
 */

interface ParsedResponse {
  mode: 'block_replace' | 'diff' | 'full_code' | null;
  code: string | null;
  diff: string | null;
  shortSummary: string | null;
  explanation: string | null;
  suggestions: string[];
  chatFormat: string | null;
  codeEditSummary: string | null;
  codeEditCount: number | null;
  codeEditCompleteSummary: string | null;
  linesReceived: number;
  isComplete: boolean;
}

interface AccumulatorState {
  accumulated: string;
  parsed: ParsedResponse;
}

const DELIMITERS = {
  CODE_EDIT_SUMMARY_START: '[CODE_EDIT_SUMMARY]',
  CODE_EDIT_SUMMARY_END: '[/CODE_EDIT_SUMMARY]',
  CODE_EDIT_COMPLETE_SUMMARY_START: '[CODE_EDIT_COMPLETE_SUMMARY]',
  CODE_EDIT_COMPLETE_SUMMARY_END: '[/CODE_EDIT_COMPLETE_SUMMARY]',
  REPLACE_START: '{@|~REPLACE}',
  REPLACE_END: '{@|~END_REPLACE}',
  DIFF_START: '[DIFF]',
  DIFF_END: '[/DIFF]',
  CODE_START: '[CODE]',
  CODE_END: '[/CODE]',
  SHORT_SUMMARY_START: '[SHORT_SUMMARY]',
  SHORT_SUMMARY_END: '[/SHORT_SUMMARY]',
  EXPLANATION_START: '[EXPLANATION]',
  EXPLANATION_END: '[/EXPLANATION]',
  SUGGESTIONS_START: '[SUGGESTIONS]',
  SUGGESTIONS_END: '[/SUGGESTIONS]',
  CHAT_FORMAT_START: '[CHAT_FORMAT]',
  CHAT_FORMAT_END: '[/CHAT_FORMAT]',
};

/**
 * Create a new streaming code accumulator
 */
export function createStreamingAccumulator(): AccumulatorState {
  return {
    accumulated: '',
    parsed: {
      mode: null,
      code: null,
      diff: null,
      shortSummary: null,
      explanation: null,
      suggestions: [],
      chatFormat: null,
      codeEditSummary: null,
      codeEditCount: null,
      codeEditCompleteSummary: null,
      linesReceived: 0,
      isComplete: false,
    },
  };
}

/**
 * Add a chunk to the accumulator and parse available sections
 */
export function addChunk(
  state: AccumulatorState,
  chunk: string
): AccumulatorState {
  const newAccumulated = state.accumulated + chunk;
  const parsed = parseAccumulated(newAccumulated);

  return {
    accumulated: newAccumulated,
    parsed,
  };
}

/**
 * Parse the accumulated content for complete sections
 */
function parseAccumulated(content: string): ParsedResponse {
  const result: ParsedResponse = {
    mode: null,
    code: null,
    diff: null,
    shortSummary: null,
    explanation: null,
    suggestions: [],
    chatFormat: null,
    codeEditSummary: null,
    codeEditCount: null,
    codeEditCompleteSummary: null,
    linesReceived: 0,
    isComplete: false,
  };

  // Extract code edit summary
  const codeEditSummaryMatch = extractSection(
    content,
    DELIMITERS.CODE_EDIT_SUMMARY_START,
    DELIMITERS.CODE_EDIT_SUMMARY_END
  );
  if (codeEditSummaryMatch) {
    result.codeEditSummary = codeEditSummaryMatch.trim().substring(0, 100);
  }

  // Extract code edit complete summary
  const codeEditCompleteSummaryMatch = extractSection(
    content,
    DELIMITERS.CODE_EDIT_COMPLETE_SUMMARY_START,
    DELIMITERS.CODE_EDIT_COMPLETE_SUMMARY_END
  );
  if (codeEditCompleteSummaryMatch) {
    result.codeEditCompleteSummary = codeEditCompleteSummaryMatch.trim().substring(0, 100);
  }
  
  // Extract line counts from REPLACE markers (format: {@|~REPLACE:[fromCount,withCount]})
  const replaceCountRegex = /{@\|~REPLACE:\[(\d+),(\d+)\]}/g;
  let totalWithLines = 0;
  let replaceCountMatch;
  while ((replaceCountMatch = replaceCountRegex.exec(content)) !== null) {
    const withCount = parseInt(replaceCountMatch[2], 10);
    totalWithLines += withCount;
  }
  if (totalWithLines > 0) {
    result.codeEditCount = totalWithLines;
  }

  // Check for replace, diff, or code mode
  const hasReplace = content.includes(DELIMITERS.REPLACE_START);
  const hasDiff = content.includes(DELIMITERS.DIFF_START);
  const hasCode = content.includes(DELIMITERS.CODE_START);

  if (hasReplace) {
    result.mode = 'block_replace';
    // Extract replace blocks (including partial during streaming)
    const replaceStartIndex = content.indexOf(DELIMITERS.REPLACE_START);
    if (replaceStartIndex !== -1) {
      const replaceContentStart = replaceStartIndex + DELIMITERS.REPLACE_START.length;
      const replaceEndIndex = content.indexOf(DELIMITERS.REPLACE_END, replaceStartIndex);
      
      if (replaceEndIndex !== -1) {
        // Replace section is complete
        const replaceContent = content.substring(replaceContentStart, replaceEndIndex);
        result.diff = replaceContent.trim(); // Store in diff field for compatibility
        // Count replacement lines (lines in WITH sections)
        result.linesReceived = countReplaceChanges(replaceContent.trim());
      } else {
        // Replace section is still streaming - count what we have so far
        const partialReplace = content.substring(replaceContentStart);
        result.diff = null; // Not complete yet
        result.linesReceived = countReplaceChanges(partialReplace);
      }
    }
  } else if (hasDiff) {
    result.mode = 'diff';
    // Extract diff (including partial diff during streaming)
    const diffStartIndex = content.indexOf(DELIMITERS.DIFF_START);
    if (diffStartIndex !== -1) {
      const diffContentStart = diffStartIndex + DELIMITERS.DIFF_START.length;
      const diffEndIndex = content.indexOf(DELIMITERS.DIFF_END, diffStartIndex);
      
      if (diffEndIndex !== -1) {
        // Diff section is complete
        const diffContent = content.substring(diffContentStart, diffEndIndex);
        result.diff = diffContent.trim();
        // For diffs, count only changed lines (+ and - lines, not context or @@)
        result.linesReceived = countDiffChanges(diffContent.trim());
      } else {
        // Diff section is still streaming - count what we have so far
        const partialDiff = content.substring(diffContentStart);
        result.diff = null; // Not complete yet
        result.linesReceived = countDiffChanges(partialDiff);
      }
    }
  } else if (hasCode) {
    result.mode = 'full_code';
    // Extract code (including partial code during streaming)
    const codeStartIndex = content.indexOf(DELIMITERS.CODE_START);
    if (codeStartIndex !== -1) {
      const codeContentStart = codeStartIndex + DELIMITERS.CODE_START.length;
      const codeEndIndex = content.indexOf(DELIMITERS.CODE_END, codeStartIndex);
      
      if (codeEndIndex !== -1) {
        // Code section is complete
        const codeContent = content.substring(codeContentStart, codeEndIndex);
        result.code = codeContent.trim();
        result.linesReceived = countLines(codeContent.trim());
      } else {
        // Code section is still streaming - count what we have so far
        const partialCode = content.substring(codeContentStart);
        result.code = null; // Not complete yet
        result.linesReceived = countLines(partialCode);
      }
    }
  }

  // Extract short summary
  const shortSummaryMatch = extractSection(
    content,
    DELIMITERS.SHORT_SUMMARY_START,
    DELIMITERS.SHORT_SUMMARY_END
  );
  if (shortSummaryMatch) {
    result.shortSummary = shortSummaryMatch.trim();
  }

  // Extract explanation
  const explanationMatch = extractSection(
    content,
    DELIMITERS.EXPLANATION_START,
    DELIMITERS.EXPLANATION_END
  );
  if (explanationMatch) {
    result.explanation = explanationMatch.trim();
  }

  // Extract suggestions
  const suggestionsMatch = extractSection(
    content,
    DELIMITERS.SUGGESTIONS_START,
    DELIMITERS.SUGGESTIONS_END
  );
  if (suggestionsMatch) {
    result.suggestions = suggestionsMatch
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  // Extract chat format
  const chatFormatMatch = extractSection(
    content,
    DELIMITERS.CHAT_FORMAT_START,
    DELIMITERS.CHAT_FORMAT_END
  );
  if (chatFormatMatch) {
    result.chatFormat = chatFormatMatch.trim();
  }

  // Check if response is complete
  result.isComplete =
    ((result.mode === 'full_code' && result.code !== null) ||
     (result.mode === 'diff' && result.diff !== null) ||
     (result.mode === 'block_replace' && result.diff !== null)) &&
    result.explanation !== null &&
    content.includes(DELIMITERS.CHAT_FORMAT_END);

  return result;
}

/**
 * Extract content between start and end delimiters
 */
function extractSection(
  content: string,
  startDelimiter: string,
  endDelimiter: string
): string | null {
  const startIndex = content.indexOf(startDelimiter);
  if (startIndex === -1) {
    return null;
  }

  const endIndex = content.indexOf(endDelimiter, startIndex);
  if (endIndex === -1) {
    // End delimiter not found yet (incomplete)
    return null;
  }

  const contentStart = startIndex + startDelimiter.length;
  return content.substring(contentStart, endIndex);
}

/**
 * Get the percentage of completion based on delimiter presence
 */
export function getCompletionPercentage(state: AccumulatorState): number {
  const content = state.accumulated;
  let count = 0;
  const totalDelimiters = 5; // code/diff/replace, summary, explanation, suggestions, chatFormat

  // Check for CODE, DIFF, or REPLACE completion
  if (content.includes(DELIMITERS.CODE_END) || 
      content.includes(DELIMITERS.DIFF_END) || 
      content.includes(DELIMITERS.REPLACE_END)) count++;
  if (content.includes(DELIMITERS.SHORT_SUMMARY_END)) count++;
  if (content.includes(DELIMITERS.EXPLANATION_END)) count++;
  if (content.includes(DELIMITERS.SUGGESTIONS_END)) count++;
  if (content.includes(DELIMITERS.CHAT_FORMAT_END)) count++;

  return Math.round((count / totalDelimiters) * 100);
}

/**
 * Check if we have enough content to show a preview
 */
export function hasPreviewableCode(state: AccumulatorState): boolean {
  return state.parsed.code !== null && state.parsed.code.length > 100;
}

/**
 * Count lines in code
 */
function countLines(code: string): number {
  if (!code) return 0;
  return code.split('\n').length;
}

/**
 * Count actual changes in diff (only + and - lines, not context or @@)
 */
function countDiffChanges(diffText: string): number {
  if (!diffText) return 0;
  
  const lines = diffText.split('\n');
  let changeCount = 0;
  
  for (const line of lines) {
    // Count lines that start with + or - (actual changes)
    if (line.startsWith('+') || line.startsWith('-')) {
      changeCount++;
    }
  }
  
  return changeCount;
}

/**
 * Count actual changes in block-replace (lines in WITH sections)
 */
function countReplaceChanges(replaceText: string): number {
  if (!replaceText) return 0;
  
  // Count lines between {@|~WITH~BEGIN:*} and {@|~WITH~END:*}
  const withSections = replaceText.match(/{@\|~WITH~BEGIN:\d+}([\s\S]*?){@\|~WITH~END:\d+}/g);
  if (!withSections) return 0;
  
  let totalLines = 0;
  for (const section of withSections) {
    // Extract content between markers
    const contentMatch = section.match(/{@\|~WITH~BEGIN:\d+}([\s\S]*?){@\|~WITH~END:\d+}/);
    if (contentMatch) {
      const content = contentMatch[1].trim();
      totalLines += content.split('\n').length;
    }
  }
  
  return totalLines;
}

/**
 * Get a display-friendly summary of current progress
 */
export function getProgressSummary(state: AccumulatorState): string {
  const { parsed } = state;
  
  if (parsed.isComplete) {
    return 'Complete';
  }
  
  if (parsed.code) {
    if (parsed.explanation) {
      return 'Finalizing...';
    }
    return 'Generating explanation...';
  }
  
  if (state.accumulated.includes(DELIMITERS.CODE_START) || 
      state.accumulated.includes(DELIMITERS.DIFF_START) ||
      state.accumulated.includes(DELIMITERS.REPLACE_START)) {
    return 'Writing code...';
  }
  
  return 'Starting...';
}

/**
 * Calculate progress percentage based on lines received vs estimated
 */
export function calculateProgress(linesReceived: number, estimatedLines: number): number {
  if (estimatedLines <= 0) return 0;
  const progress = (linesReceived / estimatedLines) * 100;
  // Cap at 99% until complete event
  return Math.min(Math.round(progress), 99);
}

